import { aluminium } from './aluminium'
import { antimony } from './antimony'
import { arsenic } from './arsenic'
import { barium } from './barium'
import { boron } from './boron'
import { cadmium } from './cadmium'
import { chromium } from './chromium'
import { cobalt } from './cobalt'
import { copper } from './copper'
import { gallium } from './gallium'
import { germanium } from './germanium'
import { gold } from './gold'
import { graphite } from './graphite'
import { hafnium } from './hafnium'
import { indium } from './indium'
import { iron } from './iron'
import { lead } from './lead'
import { lithium } from './lithium'
import { magnesium } from './magnesium'
import { manganese } from './manganese'
import { nickel } from './nickel'
import { niobium } from './niobium'
import { palladium } from './palladium'
import { platinum } from './platinum'
import { potassium } from './potassium'
import { rareEarth } from './rare-earth'
import { ruthenium } from './ruthenium'
import { silicon } from './silicon'
import { silver } from './silver'
import { strontium } from './strontium'
import { tantalum } from './tantalum'
import { tellurium } from './tellurium'
import { tin } from './tin'
import { titanium } from './titanium'
import { tungsten } from './tungsten'
import type { Metal } from './types'
import { vanadium } from './vanadium'
import { zinc } from './zinc'
import { zirconium } from './zirconium'

export type { Metal, MetalMine, I18nDescriptor } from './types'

// Declared before the helpers that read it — a hand-maintained list, alphabetical.
export const METALS = [
  aluminium,
  antimony,
  arsenic,
  barium,
  boron,
  cadmium,
  chromium,
  cobalt,
  copper,
  gallium,
  germanium,
  gold,
  graphite,
  hafnium,
  indium,
  iron,
  lead,
  lithium,
  magnesium,
  manganese,
  nickel,
  niobium,
  palladium,
  platinum,
  potassium,
  rareEarth,
  ruthenium,
  silicon,
  silver,
  strontium,
  tantalum,
  tellurium,
  tin,
  titanium,
  tungsten,
  vanadium,
  zinc,
  zirconium,
]

// Latin letters NFKD leaves whole, so the accent strip below can't reach them.
const FOLD: Record<string, string> = {
  ı: 'i',
  ø: 'o',
  đ: 'd',
  ð: 'd',
  ł: 'l',
  æ: 'ae',
  œ: 'oe',
  ß: 'ss',
  þ: 'th',
}

// Stable `?mine=` id for a mine, derived from its `region` (the clean place label) so no
// `slug` field is authored on every mine. Folds diacritics before slugifying so accented
// places read cleanly ("Núi Pháo" → "nui-phao", not "n-i-ph-o") — deterministic, so it
// round-trips between writing and resolving the URL. Regions are unique within a metal (a
// collision resolves to the first).
export function mineSlug(region: string): string {
  return region
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics (accents) left by NFKD
    .toLowerCase()
    .replace(/[^a-z0-9]/g, (char) => FOLD[char] ?? char)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * The metal a slug points at, or null — the one lookup behind `/metal/:id` and `?metal=`
 * alike. Mirrors `articleBySlug`, and takes `string | null` for the same reason: callers
 * hand it a route param straight, without a guard of their own.
 */
export const metalBySlug = (slug: string | null) =>
  METALS.find((m) => m.slug === slug) ?? null

/** The metals authored as related to this one, in the order they were authored. */
export const relatedMetals = (slug: string) =>
  (metalBySlug(slug)?.related ?? [])
    .map((related) => metalBySlug(related))
    .filter((m): m is Metal => Boolean(m))
